import { type ReactNode, useState } from 'react';
import { api, SwapLimitError } from '../api';
import type { DomainDetail } from '../types';

interface Props {
  onBack: () => void;
  onCreated: (domain: DomainDetail) => void;
}

type Step = 'enter' | 'install';
type InstallPath = 'self' | 'developer_email' | 'share_link';

export function AddDomain({ onBack, onCreated }: Props) {
  const [step, setStep] = useState<Step>('enter');
  const [domain, setDomain] = useState('');
  const [domainDetail, setDomainDetail] = useState<DomainDetail | null>(null);
  const [selectedPath, setSelectedPath] = useState<InstallPath | null>(null);
  const [developerEmail, setDeveloperEmail] = useState('');
  const [developerName, setDeveloperName] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [swapLimitErr, setSwapLimitErr] = useState<SwapLimitError | null>(null);

  // ── Step 1: enter domain ────────────────────────────────────────────────

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const raw = domain.trim().toLowerCase().replace(/^https?:\/\//i, '').replace(/\/.*$/, '').replace(/^www\./, '');
      const detail = await api.domains.create(raw);
      setDomainDetail(detail);
      setStep('install');
    } catch (err) {
      if (err instanceof SwapLimitError) {
        setSwapLimitErr(err);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to create domain');
      }
    } finally {
      setLoading(false);
    }
  }

  // ── Step 2: choose install path ─────────────────────────────────────────

  async function handleSelectPath(path: InstallPath) {
    setSelectedPath(path);
    if (domainDetail) {
      await api.domains.setInstallPath(domainDetail.id, path).catch(() => null);
    }
  }

  async function handleSendDeveloperEmail(e: React.FormEvent) {
    e.preventDefault();
    if (!domainDetail) return;
    setLoading(true);
    setError('');
    try {
      await api.domains.sendDeveloperEmail(domainDetail.id, developerEmail, developerName || undefined);
      setEmailSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send email');
    } finally {
      setLoading(false);
    }
  }

  function copySnippet() {
    if (!domainDetail) return;
    navigator.clipboard.writeText(domainDetail.installSnippet).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function copyShareLink() {
    if (!domainDetail) return;
    const link = `https://dashboard.fixmyseo.io/install/${domainDetail.id}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="bg-brand-paper">
      <div className="max-w-xl mx-auto px-4 pt-10 pb-20">
        <button onClick={onBack} className="text-sm text-gray-400 hover:text-brand-ink mb-6 inline-flex items-center gap-1">
          ← Back
        </button>

        {/* ── Step 1: Enter domain ── */}
        {step === 'enter' && (
          <div className="animate-fade-in">
            <h1 className="text-2xl font-semibold text-brand-ink mb-2">Add a domain</h1>
            <p className="text-gray-500 text-sm mb-8">Enter the domain you want fixmyseo to optimise for AI visibility.</p>

            {error && <p className="mb-4 text-sm text-red-600 bg-red-50 rounded-lg px-4 py-3">{error}</p>}

            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label htmlFor="domain" className="block text-sm font-medium text-brand-ink mb-1">
                  Domain
                </label>
                <input
                  id="domain"
                  type="text"
                  placeholder="example.com"
                  required
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand-green"
                />
                <p className="text-xs text-gray-400 mt-1">No http:// or www needed.</p>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand-green text-white font-medium rounded-lg py-2.5 text-sm hover:bg-brand-green-dark transition-colors disabled:opacity-50"
              >
                {loading ? 'Adding…' : 'Add domain →'}
              </button>
            </form>
          </div>
        )}

        {/* ── Step 2: Choose install path ── */}
        {step === 'install' && domainDetail && (
          <div className="animate-fade-in">
            <h1 className="text-2xl font-semibold text-brand-ink mb-2">Install the snippet</h1>
            <p className="text-gray-500 text-sm mb-6">
              Add one line of code to <strong>{domainDetail.domain}</strong>. Choose how you'd like to do it.
            </p>

            {error && <p className="mb-4 text-sm text-red-600 bg-red-50 rounded-lg px-4 py-3">{error}</p>}

            {/* Path selector */}
            {!selectedPath && (
              <div className="space-y-3">
                <PathCard
                  title="I'll add it myself"
                  description={<>Copy the code snippet and paste it into your site&#39;s <code className="bg-gray-100 px-1 rounded">&lt;head&gt;</code>.</>}
                  onClick={() => handleSelectPath('self')}
                />
                <PathCard
                  title="Send to my developer"
                  description="We'll email the snippet directly to your developer."
                  onClick={() => handleSelectPath('developer_email')}
                />
                <PathCard
                  title="Share installation link"
                  description="Generate a shareable link with full instructions."
                  onClick={() => handleSelectPath('share_link')}
                />
              </div>
            )}

            {/* Self-install */}
            {selectedPath === 'self' && (
              <div className="animate-fade-in space-y-4">
                <p className="text-sm text-gray-600">
                  Paste this into the <code className="bg-gray-100 px-1 rounded">&lt;head&gt;</code> of every page on {domainDetail.domain}:
                </p>
                <div className="relative">
                  <pre className="bg-brand-ink text-brand-paper text-xs rounded-lg p-4 overflow-x-auto font-mono">
                    {domainDetail.installSnippet}
                  </pre>
                  <button
                    onClick={copySnippet}
                    className="absolute top-2 right-2 text-xs bg-white/10 hover:bg-white/20 text-white px-2 py-1 rounded transition-colors"
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <div className="text-sm text-gray-500 space-y-1">
                  <p className="font-medium text-brand-ink">Platform guides:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li><a href="https://support.google.com/sites/answer/2722257" target="_blank" rel="noopener noreferrer" className="text-brand-green hover:underline">Google Sites</a></li>
                    <li><a href="https://support.squarespace.com/hc/en-us/articles/205815908" target="_blank" rel="noopener noreferrer" className="text-brand-green hover:underline">Squarespace</a></li>
                    <li><a href="https://help.wix.com/en/article/embedding-custom-code-to-your-site" target="_blank" rel="noopener noreferrer" className="text-brand-green hover:underline">Wix</a></li>
                    <li>WordPress: add to your theme's <code className="bg-gray-100 px-1 rounded">functions.php</code> via <code className="bg-gray-100 px-1 rounded">wp_head</code> hook, or use a header/footer plugin.</li>
                  </ul>
                </div>
                <button
                  onClick={() => domainDetail && onCreated(domainDetail)}
                  className="w-full bg-brand-green text-white font-medium rounded-lg py-2.5 text-sm hover:bg-brand-green-dark transition-colors"
                >
                  Snippet added — go to dashboard →
                </button>
                <button onClick={() => setSelectedPath(null)} className="w-full text-sm text-gray-400 hover:text-brand-ink">
                  ← Choose a different path
                </button>
              </div>
            )}

            {/* Developer email */}
            {selectedPath === 'developer_email' && (
              <div className="animate-fade-in space-y-4">
                {!emailSent ? (
                  <form onSubmit={handleSendDeveloperEmail} className="space-y-4">
                    <div>
                      <label htmlFor="dev-email" className="block text-sm font-medium text-brand-ink mb-1">Developer email</label>
                      <input
                        id="dev-email"
                        type="email"
                        required
                        value={developerEmail}
                        onChange={(e) => setDeveloperEmail(e.target.value)}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand-green"
                        placeholder="dev@example.com"
                      />
                    </div>
                    <div>
                      <label htmlFor="dev-name" className="block text-sm font-medium text-brand-ink mb-1">Developer name <span className="text-gray-400 font-normal">(optional)</span></label>
                      <input
                        id="dev-name"
                        type="text"
                        value={developerName}
                        onChange={(e) => setDeveloperName(e.target.value)}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand-green"
                        placeholder="Alex"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-brand-green text-white font-medium rounded-lg py-2.5 text-sm hover:bg-brand-green-dark transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Sending…' : 'Send email →'}
                    </button>
                  </form>
                ) : (
                  <div className="bg-green-50 rounded-xl p-6 text-center">
                    <p className="text-brand-green font-medium mb-1">Email sent ✓</p>
                    <p className="text-sm text-gray-600">We've sent the installation instructions to <strong>{developerEmail}</strong>.</p>
                  </div>
                )}
                {emailSent && (
                  <button
                    onClick={() => domainDetail && onCreated(domainDetail)}
                    className="w-full bg-brand-green text-white font-medium rounded-lg py-2.5 text-sm hover:bg-brand-green-dark transition-colors"
                  >
                    Email sent — go to dashboard →
                  </button>
                )}
                <button onClick={() => setSelectedPath(null)} className="w-full text-sm text-gray-400 hover:text-brand-ink">
                  ← Choose a different path
                </button>
              </div>
            )}

            {/* Share link */}
            {selectedPath === 'share_link' && (
              <div className="animate-fade-in space-y-4">
                <p className="text-sm text-gray-600">
                  Share this link — it includes the snippet and full installation instructions:
                </p>
                <div className="relative">
                  <input
                    readOnly
                    value={`https://dashboard.fixmyseo.io/install/${domainDetail.id}`}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-brand-ink bg-gray-50 pr-20"
                  />
                  <button
                    onClick={copyShareLink}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-xs bg-brand-green text-white px-3 py-1 rounded hover:bg-brand-green-dark transition-colors"
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <button
                  onClick={() => domainDetail && onCreated(domainDetail)}
                  className="w-full bg-brand-green text-white font-medium rounded-lg py-2.5 text-sm hover:bg-brand-green-dark transition-colors"
                >
                  Link copied — go to dashboard →
                </button>
                <button onClick={() => setSelectedPath(null)} className="w-full text-sm text-gray-400 hover:text-brand-ink">
                  ← Choose a different path
                </button>
              </div>
            )}
          </div>
        )}

      </div>

      {/* Swap limit modal */}
      {swapLimitErr && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-7 max-w-sm w-full shadow-xl">
            <p className="text-lg font-semibold text-brand-ink mb-2">Domain swap limit reached</p>
            <p className="text-sm text-gray-600 mb-4">{swapLimitErr.message}</p>
            <p className="text-xs text-gray-400 mb-5">
              Resets on {new Date(swapLimitErr.next_reset_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}.
            </p>
            <div className="flex gap-3">
              {swapLimitErr.upgrade_url && (
                <a
                  href={swapLimitErr.upgrade_url}
                  className="flex-1 text-center text-sm font-medium bg-brand-green text-white px-4 py-2.5 rounded-lg hover:bg-brand-green-dark transition-colors"
                >
                  Upgrade plan
                </a>
              )}
              <button
                onClick={() => setSwapLimitErr(null)}
                className="flex-1 text-sm text-gray-600 hover:text-gray-800 px-4 py-2.5 border border-gray-200 rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PathCard({ title, description, onClick }: { title: string; description: ReactNode; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-white border border-gray-100 rounded-xl px-5 py-4 hover:border-brand-green transition-colors shadow-sm"
    >
      <p className="font-medium text-brand-ink text-sm mb-0.5">{title}</p>
      <p className="text-xs text-gray-500">{description}</p>
    </button>
  );
}
